import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ENDPOINTS, type ContractTypeAutoApprovalResponse, type SetContractTypeAutoApprovalResponse } from '@clm/shared-types';

import { fetchContractTypeAutoApproval, setContractTypeAutoApproval } from '../../api/verification-policy';
import { PanelProblem } from '../quality/panel';
import { toProblem, type Panel } from '../quality/panel-state';
import { AUTO_APPROVAL_STATE_COPY, SWITCH_ON_REFUSAL_COPY } from '../type-proposal-copy';
import {
  AGAINST_NAME_DISPLAY_ONLY_COPY,
  COUNTED_CASES_COPY,
  SWITCH_ON_NOTE_COPY,
  TYPE_SWITCH_FIXED_FIGURES_COPY,
  TYPE_SWITCH_INTRO_COPY,
  typeBasisLine,
  typeGrainCopy,
  typeStateSentence,
  typeSwitchProblemOf,
} from './contract-type-shared';
import { PolicyProblemNotice } from './PolicyProblemNotice';
import { SCOPE_ALL_REQUIRED_COPY, type PolicyProblem } from './shared';

const PATH = ENDPOINTS['contract-type-auto-approval'].path;
const PANEL = 'Contract type auto-approval';

type Outcome =
  | { readonly kind: 'done'; readonly response: SetContractTypeAutoApprovalResponse }
  | { readonly kind: 'problem'; readonly problem: PolicyProblem };

/**
 * The Auto-approval page's **Contract type** card — CLM-121 (D-110-4 points 3
 * and 6; D-110-3 point 3; R-121-13 … R-121-16).
 *
 * One read, `GET /v1/admin/contract-type-auto-approval`, and one write,
 * `set-contract-type-auto-approval`. Rendered by the page only for a sign-in
 * that may hold `admin:configure`; a 403 renders nothing (the Needs Attention
 * panel's rule), so Legal sees no card.
 *
 *  - **The switch.** On or off, its revision, who switched it and when, and the
 *    note. The four numbers are printed as fixed — D-110-4's, never settings.
 *  - **The state in words**, from the API: lapsed (the reading that lapsed it),
 *    paused (the correction and who made it), stale (since when), held, or on.
 *  - **The figures.** The counted decisions on contracts whose file name gave no
 *    type, and — marked display only — "AI against the file name". Below 30 the
 *    rate reads "too few decisions", never a number (CLM-107's rule).
 *  - **The activity** of the last 30 days, the types approved by policy in all,
 *    and the open safety samples, linked to Type proposals.
 *  - **The action.** Switch on… (a note is required; disabled, with the refusal
 *    in words, when the API says it is not offered) or Switch off. A receipt
 *    after a write; the card re-reads, keeping the last answer on screen.
 *
 * A practice-scoped Admin sees the switch and the Scope-All notice in place of
 * the figures and the action (R-121-16): the figure is org-wide.
 */
export function ContractTypeAutoApprovalCard() {
  const [panel, setPanel] = useState<Panel<ContractTypeAutoApprovalResponse>>({ kind: 'loading' });
  const [shown, setShown] = useState<ContractTypeAutoApprovalResponse | null>(null);
  const [nonce, setNonce] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setPanel({ kind: 'loading' });
    void (async () => {
      try {
        const response = await fetchContractTypeAutoApproval(controller.signal);
        if (controller.signal.aborted) return;
        setPanel({ kind: 'ready', response });
        setShown(response);
      } catch (error) {
        if (controller.signal.aborted) return;
        setPanel(toProblem(error));
        setShown(null);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [nonce]);

  const reload = () => {
    setNonce((n) => n + 1);
  };

  if (panel.kind === 'problem' && panel.problem === 'forbidden') return null;

  return (
    <section className="card" aria-labelledby="vp-type-heading" data-testid="vp-contract-type">
      <h3 id="vp-type-heading">Contract type</h3>
      <p className="mut">{TYPE_SWITCH_INTRO_COPY}</p>
      <p className="mut" data-testid="vp-type-fixed">
        {TYPE_SWITCH_FIXED_FIGURES_COPY}
      </p>

      {panel.kind === 'loading' && shown === null ? (
        <p role="status" aria-busy="true">
          Reading the contract type’s switch…
        </p>
      ) : null}
      {panel.kind === 'problem' ? <PanelProblem panel={PANEL} state={panel} endpointPath={PATH} onRetry={reload} /> : null}

      {shown === null ? null : (
        <TypeSwitchBody
          response={shown}
          onOutcome={(next) => {
            setOutcome(next);
          }}
          onChanged={reload}
        />
      )}

      <div aria-live="polite" data-testid="vp-type-outcome">
        {outcome?.kind === 'done' ? (
          <p className="banner up" role="status">
            Recorded revision r{outcome.response.revision.policy_revision}: auto-approval for the contract type is{' '}
            {outcome.response.revision.on ? 'on' : 'off'}. State now: {AUTO_APPROVAL_STATE_COPY[outcome.response.state]}{' '}
            (audit row {outcome.response.audit.audit_id}).
            {outcome.response.revision.on ? null : ' Types already approved by policy stay approved.'}
          </p>
        ) : null}
        {outcome?.kind === 'problem' ? <PolicyProblemNotice problem={outcome.problem} /> : null}
      </div>
    </section>
  );
}

function TypeSwitchBody({
  response,
  onOutcome,
  onChanged,
}: {
  readonly response: ContractTypeAutoApprovalResponse;
  readonly onOutcome: (outcome: Outcome) => void;
  readonly onChanged: () => void;
}) {
  const current = response.switch.current;
  const on = current?.on ?? false;
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const offer = response.switch_on;
  const sentence = typeStateSentence(response);

  async function act(next: 'on' | 'off') {
    const trimmed = note.trim();
    if (next === 'on' && trimmed === '') {
      setNoteError(SWITCH_ON_NOTE_COPY);
      return;
    }
    setNoteError(null);
    setBusy(true);
    try {
      const result = await setContractTypeAutoApproval({
        switch: next,
        // The revision this card showed: another Admin's change meanwhile is 409 policy_changed, never an overwrite.
        expected_policy_revision: current?.policy_revision ?? 0,
        ...(next === 'on' ? { change_note: trimmed } : {}),
      });
      onOutcome({ kind: 'done', response: result });
      setAsking(false);
      setNote('');
      onChanged();
    } catch (error) {
      const problem = typeSwitchProblemOf(error);
      onOutcome({ kind: 'problem', problem });
      if (problem.refusal?.reload === true) onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p data-testid="vp-type-switch">
        <strong>{on ? 'On' : 'Off'}</strong>
        {current === null ? (
          <span className="mut"> — no administrator has switched it on (no revision is off).</span>
        ) : (
          <span className="mut">
            {' '}
            — revision r{current.policy_revision}, by {current.changed_by_upn} on{' '}
            <time dateTime={current.changed_utc}>{current.changed_utc.slice(0, 10)}</time>
            {current.grain === null ? null : <>, at {typeGrainCopy(current.grain)}</>}
            {current.change_note === null ? null : <>: “{current.change_note}”</>}
          </span>
        )}
      </p>

      {response.withheld !== null ? (
        <p className="banner up" role="status" data-testid="vp-type-withheld">
          {SCOPE_ALL_REQUIRED_COPY}
        </p>
      ) : (
        <>
          {sentence === null ? null : (
            <p data-testid="vp-type-state" data-state={response.state ?? undefined}>
              {sentence}
            </p>
          )}

          <dl className="mut" data-testid="vp-type-figures">
            <dt>Current model and schema version</dt>
            <dd>{typeGrainCopy(response.grain)}</dd>
            {response.basis === null ? null : (
              <>
                <dt>Counted decisions ({COUNTED_CASES_COPY})</dt>
                <dd data-testid="vp-type-basis">{typeBasisLine(response.basis)}</dd>
              </>
            )}
            {response.against_file_name === null ? null : (
              <>
                <dt>AI against the file name ({AGAINST_NAME_DISPLAY_ONLY_COPY})</dt>
                <dd data-testid="vp-type-against-name">{typeBasisLine(response.against_file_name)}</dd>
              </>
            )}
          </dl>

          {response.activity === null ? null : (
            <p className="mut" data-testid="vp-type-activity">
              Last 30 days: {response.activity.last_30_days.approved} approved by policy ·{' '}
              {response.activity.last_30_days.sampled} sent as safety samples ·{' '}
              {response.activity.last_30_days.sent_to_admin} sent to an administrator for another reason. Approved by
              policy in all: {response.activity.policy_approved_total}. Open safety samples:{' '}
              {response.activity.open_samples}
              {response.activity.open_samples === 0 ? null : (
                <>
                  {' '}
                  — <Link to="/admin/type-proposals">check them on Type proposals</Link>
                </>
              )}
              .
            </p>
          )}

          <div className="vacts">
            {on ? (
              <button type="button" className="btn" disabled={busy} onClick={() => void act('off')}>
                {busy ? 'Saving…' : 'Switch off'}
              </button>
            ) : null}
            {offer === null ? null : (
              <button
                type="button"
                className="btn pri"
                disabled={busy || !offer.allowed}
                aria-expanded={asking}
                onClick={() => {
                  setAsking(true);
                  setNoteError(null);
                }}
              >
                {on ? 'Switch on again…' : 'Switch on…'}
              </button>
            )}
          </div>
          {offer !== null && !offer.allowed && offer.refusal !== null ? (
            <p className="mut" data-testid="vp-type-switch-on-refusal" data-code={offer.refusal}>
              {SWITCH_ON_REFUSAL_COPY[offer.refusal]}
            </p>
          ) : null}

          {asking && offer?.allowed === true ? (
            <div className="vpanel" role="group" aria-labelledby="vp-type-on-heading" data-testid="vp-type-on-dialog">
              <h4 id="vp-type-on-heading">Switch on auto-approval for the contract type</h4>
              <p className="mut">
                Taken at {typeGrainCopy(response.grain)}, on the counted figure{' '}
                {response.basis === null ? null : typeBasisLine(response.basis)}. A new model or schema version lapses it.
              </p>
              <div className="field">
                <label htmlFor="vp-type-note">Why (required)</label>
                <textarea
                  id="vp-type-note"
                  rows={2}
                  maxLength={500}
                  value={note}
                  disabled={busy}
                  onChange={(event) => setNote(event.target.value)}
                />
                <p className="mut">{SWITCH_ON_NOTE_COPY}</p>
              </div>
              <div className="vacts">
                <button type="button" className="btn pri" disabled={busy} onClick={() => void act('on')}>
                  {busy ? 'Saving…' : 'Switch on'}
                </button>
                <button type="button" className="btn qt" disabled={busy} onClick={() => setAsking(false)}>
                  Cancel
                </button>
              </div>
              {noteError === null ? null : (
                <p className="ferr" role="alert">
                  {noteError}
                </p>
              )}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
